'use client';

import { useState, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import type { RowSelectionState, SortingState } from '@tanstack/react-table';
import { useTable } from '@tanstack/react-table';
import type { ColumnDef } from '@/lib/table-features';
import { Plus, Pencil, Trash2, Loader2, FlaskConical } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { DataTable } from '@/components/ui/table/data-table';
import { DataTableToolbar } from '@/components/ui/table/data-table-toolbar';
import { DataTableColumnHeader } from '@/components/ui/table/data-table-column-header';
import { useHashDialog, useHashParamDialog } from '@/hooks/use-hash-dialog';
import { appTableFeatures } from '@/lib/table-features';
import { cn } from '@/lib/utils';
import { LABS_HASH } from '../constants';
import { labQueries } from '../services/labs.queries';
import { useUpdateLabsMutation } from '../services/labs.mutations';
import type { OfficeBiLabel } from '@/features/configurations';
import type { OfficeRow } from '../types';
import { PermissionGate } from '@/features/auth';
import { usePermissions } from '@/hooks/use-permissions';
import { LabsFormDialog } from './labs-form-dialog';
import { LabsTableActionBar } from './labs-table-action-bar';

function normaliseOffices(raw: unknown): OfficeBiLabel[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => {
    if (typeof item === 'string') {
      return { ar: item, en: '', status: 'active' as const };
    }
    return {
      ar: (item as OfficeBiLabel).ar ?? '',
      en: (item as OfficeBiLabel).en ?? '',
      status: (item as OfficeBiLabel).status ?? 'active'
    };
  });
}

export function LabsPageContent() {
  const t = useTranslations('configurations');
  const tCommon = useTranslations('common');

  const { data, isLoading } = useQuery(labQueries.all());
  const updateMutation = useUpdateLabsMutation();
  // `#create` / `#edit/<idx>` / `#delete/<idx>` are deep-linkable, so a
  // view-only account could open the form by URL even with every trigger
  // hidden. The write is `PUT /labs`, which accepts `SuperAdmin|labs.edit`.
  const { hasPermission } = usePermissions();
  const canEditLabs = hasPermission('labs.edit');
  const isPending = updateMutation.isPending;

  const raw = (data as any)?.data ?? data ?? {};
  const offices: OfficeBiLabel[] = useMemo(
    () => normaliseOffices(raw.offices),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(raw.offices)]
  );

  // Overlay open-state lives in the URL hash (#create, #edit/<idx>,
  // #delete/<idx>). Offices have no DB id — they're keyed by their array index
  // (`_idx`), which rides as the hash param. Destructure the stable callbacks.
  const { open: createOpen, onOpenChange: toggleCreate } = useHashDialog(
    LABS_HASH.create
  );
  const {
    open: editOpen,
    param: editIdx,
    openWith: openEditHash,
    onOpenChange: closeEdit
  } = useHashParamDialog(LABS_HASH.edit);
  const {
    open: deleteOpen,
    param: deleteIdx,
    openWith: openDeleteHash,
    onOpenChange: closeDelete
  } = useHashParamDialog(LABS_HASH.delete);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [sorting, setSorting] = useState<SortingState>([]);

  const officeRows = useMemo<OfficeRow[]>(
    () => offices.map((o, i) => ({ ...o, _idx: i })),
    [offices]
  );

  // Resolve the hash index param back to the office row (by array index).
  const editTarget =
    editOpen && editIdx !== null
      ? (officeRows.find((o) => String(o._idx) === editIdx) ?? null)
      : null;
  const deleteTarget =
    deleteOpen && deleteIdx !== null
      ? (officeRows.find((o) => String(o._idx) === deleteIdx) ?? null)
      : null;
  const editingOffice: OfficeBiLabel | null = editTarget
    ? { ar: editTarget.ar, en: editTarget.en, status: editTarget.status }
    : null;

  function save(next: OfficeBiLabel[]) {
    updateMutation.mutate({ offices: next });
  }

  function handleAdd(office: OfficeBiLabel) {
    save([...offices, office]);
  }

  function handleEdit(office: OfficeBiLabel) {
    save(offices.map((o) => (o.ar === editingOffice?.ar ? office : o)));
  }

  function handleRemove(idx: number) {
    save(offices.filter((_, i) => i !== idx));
    closeDelete(false);
  }

  function handleBulkRemove(indices: number[]) {
    const set = new Set(indices);
    save(offices.filter((_, i) => !set.has(i)));
    setRowSelection({});
  }

  const existingAr = useMemo(() => offices.map((o) => o.ar), [offices]);

  const columns = useMemo<ColumnDef<OfficeRow>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label='Select all'
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label='Select row'
          />
        ),
        enableSorting: false,
        enableHiding: false,
        size: 40
      },
      {
        id: 'ar',
        accessorKey: 'ar',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('arabicName')} />
        ),
        cell: ({ row }) => (
          <span className='font-medium' dir='rtl'>
            {row.original.ar}
          </span>
        ),
        enableColumnFilter: true,
        meta: {
          label: t('arabicName'),
          variant: 'text' as const
        }
      },
      {
        id: 'en',
        accessorKey: 'en',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('englishName')} />
        ),
        cell: ({ row }) => (
          <span className='text-muted-foreground text-sm'>
            {row.original.en || '—'}
          </span>
        )
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('status')} />
        ),
        cell: ({ row }) => (
          <Badge
            variant={row.original.status === 'active' ? 'default' : 'secondary'}
          >
            {row.original.status === 'active'
              ? t('statusActive')
              : t('statusInactive')}
          </Badge>
        ),
        enableColumnFilter: true,
        meta: {
          label: t('status'),
          variant: 'multiSelect' as const,
          icon: 'solar:tag-bold-duotone',
          options: [
            { label: t('statusActive'), value: 'active' },
            { label: t('statusInactive'), value: 'inactive' }
          ]
        }
      },
      {
        id: 'actions',
        header: () => <div className='text-end'>{tCommon('actions')}</div>,
        cell: ({ row }) => (
          <div className='flex items-center justify-end gap-1'>
            <PermissionGate permissions={['labs.edit']}>
              <Button
                variant='ghost'
                size='icon'
                className='h-8 w-8'
                onClick={() => openEditHash(String(row.original._idx))}
                disabled={isPending}
              >
                <Pencil className='h-4 w-4' />
              </Button>
              <Button
                variant='ghost'
                size='icon'
                className='h-8 w-8'
                onClick={() => openDeleteHash(String(row.original._idx))}
                disabled={isPending}
              >
                <Trash2 className='text-destructive h-4 w-4' />
              </Button>
            </PermissionGate>
          </div>
        ),
        enableSorting: false,
        enableHiding: false
      }
    ],
    [t, tCommon, isPending, openEditHash, openDeleteHash]
  );

  const table = useTable({
    features: appTableFeatures,
    data: officeRows,
    columns,
    state: { rowSelection, sorting },
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    getRowId: (row) => String(row._idx),
    initialState: { pagination: { pageIndex: 0, pageSize: 10 } }
  });

  if (isLoading) {
    return (
      <div className='space-y-6'>
        <Skeleton className='h-8 w-48' />
        <Skeleton className='h-4 w-72' />
        <Skeleton className='h-64 w-full' />
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-2xl font-bold tracking-tight'>{t('offices')}</h1>
        <p className='text-muted-foreground mt-1'>{t('officesDescription')}</p>
      </div>

      {/* Header actions */}
      <PermissionGate permissions={['labs.edit']}>
        <div className='flex justify-end'>
          <Button onClick={() => toggleCreate(true)} disabled={isPending}>
            <Plus className='me-2 h-4 w-4' />
            {t('addOffice')}
          </Button>
        </div>
      </PermissionGate>

      {/* Table */}
      {officeRows.length === 0 ? (
        <div className='flex flex-col items-center justify-center py-12 text-center'>
          <FlaskConical className='text-muted-foreground mb-4 h-12 w-12' />
          <p className='text-muted-foreground'>{t('noLabs')}</p>
        </div>
      ) : (
        <div
          className={cn(
            'flex min-h-[400px] flex-col',
            isPending && 'opacity-60 transition-opacity'
          )}
        >
          <DataTable
            table={table}
            actionBar={
              <LabsTableActionBar
                table={table}
                isPending={isPending}
                onBulkRemove={handleBulkRemove}
              />
            }
          >
            <DataTableToolbar table={table} />
          </DataTable>
        </div>
      )}

      {/* Add / edit dialog — #create / #edit/<idx> */}
      <LabsFormDialog
        open={canEditLabs && (createOpen || (editOpen && !!editingOffice))}
        onOpenChange={(open) => {
          if (!open) (editOpen ? closeEdit : toggleCreate)(false);
        }}
        office={editOpen ? editingOffice : null}
        existingAr={existingAr}
        onSave={editOpen ? handleEdit : handleAdd}
      />

      {/* Single-row delete confirmation — #delete/<idx> */}
      <AlertDialog
        open={canEditLabs && deleteOpen && !!deleteTarget}
        onOpenChange={closeDelete}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteConfirmDescription', { name: deleteTarget?.ar ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && handleRemove(deleteTarget._idx)}
              className={cn(
                'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              )}
            >
              {isPending && <Loader2 className='me-2 h-4 w-4 animate-spin' />}
              {tCommon('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
